// Connections (roadmap 4.x): the sources the Jarvis reads. First the university Outlook (device code).
import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { day, Label, Panel } from '../ui';
import { JarvisSpinner } from '../Busy';

export function Connections() {
  const [st, setSt] = useState<any>(null);
  const [dc, setDc] = useState<{ code: string; url: string } | null>(null);
  const [msg, setMsg] = useState('');
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const load = () => api('/api/connect').then(setSt).catch((e) => setMsg(e.message));
  useEffect(() => { load(); return () => { if (timer.current) clearInterval(timer.current); }; }, []);

  const start = async () => {
    setMsg('');
    try {
      const r = await api<{ code: string; url: string }>('/api/connect/outlook/start', {});
      setDc(r);
      timer.current = setInterval(async () => {
        const p = await api<{ state: string; message?: string; account?: string }>('/api/connect/outlook/poll', {}, { quiet: true }).catch(() => null);
        if (!p || p.state === 'waiting') return;
        if (timer.current) clearInterval(timer.current);
        setDc(null);
        setMsg(p.state === 'done' ? `Bekötve: ${p.account}. Az elmúlt 120 nap levelei most jönnek be.` : p.state === 'expired' ? 'Lejárt a kód, kezdd újra.' : `Nem sikerült: ${p.message}`);
        load();
      }, 4000);
    } catch (e: any) { setMsg(e.message); }
  };
  const off = async () => { await api('/api/connect/outlook/disconnect', {}); setMsg('Leválasztva.'); load(); };
  const o = st?.outlook;

  return (
    <Panel c="cyan">
      <Label c="cyan">Kapcsolatok</Label>
      {st?.neptun && (
        <div className="conn">
          <div className="row-split"><b>Neptun {st.neptun.user ? `(${st.neptun.user})` : ''}</b><span className={`j-chip ${st.neptun.configured && st.neptun.ok !== false && !st.neptun.error ? 'j-c-mint' : 'j-c-coral'}`}>{!st.neptun.configured ? 'nincs bekötve' : st.neptun.ok === false ? 'belépési hiba' : st.neptun.error ? 'részben hiba' : 'olvasva'}</span></div>
          <p className="muted small">óránként (7–21) az üzenetek és a vizsgák, reggel 6:40-kor minden · utolsó: {st.neptun.last_sync ? `${day(st.neptun.last_sync)} ${new Date(st.neptun.last_sync).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}` : 'még nem volt'}</p>
          {st.neptun.error && <p className="err small">{st.neptun.error}</p>}
        </div>
      )}
      {st?.discord && (
        <div className="conn">
          <div className="row-split"><b>Discord-bot {st.discord.user ? `(${st.discord.user})` : ''}</b><span className={`j-chip ${st.discord.ok ? 'j-c-mint' : 'j-c-coral'}`}>{!st.discord.configured ? 'nincs bekötve' : st.discord.ok ? 'kapcsolódva' : st.discord.ok === false ? 'hiba' : 'csatlakozik…'}</span></div>
          <p className="muted small">{st.discord.channels.length ? `figyelt csatornák: ${st.discord.channels.map((c: string) => '#' + c).join(', ')}` : 'nincs kiválasztott csatorna'}</p>
          {st.discord.error && <p className="err small">{st.discord.error}</p>}
        </div>
      )}
      {(st?.mailboxes ?? []).map((b: any) => (
        <div className="conn" key={b.name}>
          <div className="row-split"><b>{b.account}</b><span className={`j-chip ${b.error ? 'j-c-coral' : 'j-c-mint'}`}>{b.error ? 'hiba' : 'olvasva'}</span></div>
          <p className="muted small">IMAP · utolsó olvasás: {b.last_sync ? `${day(b.last_sync)} ${new Date(b.last_sync).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}` : 'még nem volt'}</p>
          {b.error && <p className="err small">{b.error}</p>}
        </div>
      ))}
      {!(st?.mailboxes ?? []).some((b: any) => b.name === 'jarvis') && (
        <div className="conn"><div className="row-split"><b>jarvis@ggyt.hu</b><span className="j-chip j-c-coral">nincs bekötve</span></div>
          <p className="muted small">A Jarvis saját címe: ide irányíthatod az egyetemi leveleket, és bármit továbbíthatsz neki. Bekötés a szerveren: <code>sudo jarvis2-secret mail-jarvis</code></p></div>
      )}
      <div className="conn">
        <div className="row-split"><b>Egyetemi Outlook (közvetlen)</b><span className={`j-chip ${o?.connected ? 'j-c-mint' : 'j-c-coral'}`}>{o?.connected ? 'bekötve' : 'nincs bekötve'}</span></div>
        {o?.connected ? (
          <>
            <p className="muted small">{o.account} · utolsó olvasás: {o.last_sync ? `${day(o.last_sync)} ${new Date(o.last_sync).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}` : 'még nem volt'}</p>
            {o.error && <p className="err small">{o.error}</p>}
            <div className="j-row">{o.error && <button className="j-btn j-btn--primary" onClick={start}>Újra bekötöm</button>}<button className="j-btn" onClick={off}>Leválasztás</button></div>
          </>
        ) : dc ? (
          <div className="device">
            <p className="small">1. Nyisd meg: <a href={dc.url} target="_blank" rel="noreferrer">{dc.url}</a></p>
            <p className="small">2. Írd be ezt a kódot, és lépj be az egyetemi fiókoddal:</p>
            <div className="device-code j-num" onClick={() => navigator.clipboard?.writeText(dc.code)}>{dc.code}</div>
            <p className="muted small"><JarvisSpinner size={16} /> Várom, hogy belépj… (a kódra koppintva másolódik)</p>
          </div>
        ) : (
          <>
            <p className="muted small">A Jarvis olvassa a beérkezett és az elküldött leveleidet (az elmúlt 120 naptól), piszkozatot írhat, de küldeni nem tud. A jelszavad nem kerül hozzá.</p>
            <button className="j-btn j-btn--primary" onClick={start}>Egyetemi Outlook bekötése</button>
          </>
        )}
        {msg && <p className="small">{msg}</p>}
      </div>
    </Panel>
  );
}
